import { useDispatch } from 'react-redux';
import {
  ContainerSpacedBetween,
  BreakdownAffinityHeader,
} from 'modules/intelligentInsights/brandAffinityInsights/components/layout/BrandAffinity.styles';
import { addPositiveToast } from 'modules/toastContainer';
import {
  BREAKDOWN_BY_AFFINITY,
  EXPORT_CSV,
  AFFINITY_BREAKDOWN_ANCHOR,
  AFFINITY_BREAKDOWN_EXPORT_CONFIG,
} from 'modules/intelligentInsights/brandAffinityInsights/constants';
import { Export24, ChevronDown16 } from 'ui/icons';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { ActionMenu } from 'ui/actionMenu';
import { AllowedActionMenuElements, MENU_ALIGNMENTS } from 'ui/actionMenu/types';
import { Body2 } from 'ui/theme';
import { useDataManagementContext } from 'modules/intelligentInsights/brandAffinityInsights/contexts/dataManagement/DataManagementContext';
import { downloadAsCsvFileWithHeaders } from 'utils/csv';
import { formatCsvFilenameWithDate } from 'modules/intelligentInsights/domain.utils';
import { CsvData, Header } from 'modules/intelligentInsights/brandAffinityInsights/types';
import { TimespanEnum } from 'modules/intelligentInsights/types';

const createCSVFile = (headers: Header[], csvData: CsvData[], fileName: string) => {
  downloadAsCsvFileWithHeaders({
    headers,
    data: csvData,
    fileName,
  });
};

const createMenuExportOptions = (
  onEngagementClick: () => void,
  onMovementClick: () => void,
  onCampaignsClick: () => void,
) => [
  {
    as: AllowedActionMenuElements.Button,
    content: <Body2>{AFFINITY_BREAKDOWN_EXPORT_CONFIG.ENGAGEMENT.TITLE}</Body2>,
    id: AFFINITY_BREAKDOWN_EXPORT_CONFIG.ENGAGEMENT.ID,
    onClick: onEngagementClick,
  },
  {
    as: AllowedActionMenuElements.Button,
    content: <Body2>{AFFINITY_BREAKDOWN_EXPORT_CONFIG.MOVEMENT.TITLE}</Body2>,
    id: AFFINITY_BREAKDOWN_EXPORT_CONFIG.MOVEMENT.ID,
    onClick: onMovementClick,
  },
  {
    as: AllowedActionMenuElements.Button,
    content: <Body2>{AFFINITY_BREAKDOWN_EXPORT_CONFIG.CAMPAIGNS.TITLE}</Body2>,
    id: AFFINITY_BREAKDOWN_EXPORT_CONFIG.CAMPAIGNS.ID,
    onClick: onCampaignsClick,
  },
];

const constructFileNameCreator = (fileName: string, timespan: TimespanEnum) => (fileDate: string) =>
  `${fileName}_${timespan}_days_${fileDate}.csv`;

export const AffinityBreakdownExportMenuId = 'affinity-breakdown-export-menu-id';
export const AffinityBreakdownHeader = () => {
  const dispatch = useDispatch();
  const {
    affinityBreakdownCSVData: { engagementData, movementData, campaigns },
    timeSpan: { selectedTimespan },
  } = useDataManagementContext();

  const handleEngagementClick = () => {
    const { headers, csvData } = engagementData;
    createCSVFile(
      headers,
      csvData,
      formatCsvFilenameWithDate(
        constructFileNameCreator(
          AFFINITY_BREAKDOWN_EXPORT_CONFIG.ENGAGEMENT.FILE_NAME,
          selectedTimespan,
        ),
      ),
    );
    dispatch(addPositiveToast(AFFINITY_BREAKDOWN_EXPORT_CONFIG.ENGAGEMENT.SUCCESS));
  };
  const handleMovementClick = () => {
    const { headers, csvData } = movementData;
    createCSVFile(
      headers,
      csvData,
      formatCsvFilenameWithDate(
        constructFileNameCreator(
          AFFINITY_BREAKDOWN_EXPORT_CONFIG.MOVEMENT.FILE_NAME,
          selectedTimespan,
        ),
      ),
    );
    dispatch(addPositiveToast(AFFINITY_BREAKDOWN_EXPORT_CONFIG.MOVEMENT.SUCCESS));
  };
  const handleCampaignsClick = () => {
    const { headers, csvData } = campaigns;
    createCSVFile(
      headers,
      csvData,
      formatCsvFilenameWithDate(
        constructFileNameCreator(
          AFFINITY_BREAKDOWN_EXPORT_CONFIG.CAMPAIGNS.FILE_NAME,
          selectedTimespan,
        ),
      ),
    );
    dispatch(addPositiveToast(AFFINITY_BREAKDOWN_EXPORT_CONFIG.CAMPAIGNS.SUCCESS));
  };

  const options = createMenuExportOptions(
    handleEngagementClick,
    handleMovementClick,
    handleCampaignsClick,
  );
  return (
    <ContainerSpacedBetween>
      <BreakdownAffinityHeader id={AFFINITY_BREAKDOWN_ANCHOR}>
        {BREAKDOWN_BY_AFFINITY}
      </BreakdownAffinityHeader>
      <ActionMenu
        id={AffinityBreakdownExportMenuId}
        options={options}
        align={MENU_ALIGNMENTS.RIGHT}
      >
        <Button variant={BUTTON_VARIANTS.SECONDARY} size={BUTTON_SIZES.LARGE}>
          <Export24 />
          {EXPORT_CSV}
          <ChevronDown16 />
        </Button>
      </ActionMenu>
    </ContainerSpacedBetween>
  );
};
